import axios from 'axios';
import environmentConfig from '../config/environment';
import { translateApiError } from '../utils/errorTranslation';
import i18n from '../i18n';

const API_BASE_URL = environmentConfig.API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const idToken = localStorage.getItem('id_token');
  const authToken = localStorage.getItem('auth_token');
  

  
  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`;
  } else if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  
  // Add tenant ID header (required by backend)
  config.headers['X-Tenant-ID'] = 'hospital-demo-001';
  return config;
});

// Handle auth errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {

    
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the session
        const { CognitoUserPool } = await import('amazon-cognito-identity-js');
        const poolData = {
          UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_PROD || 'us-west-2_6NaygtqLh',
          ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_PROD || '240r8kacjngq3bgdlpq9veabs8'
        };
        const userPool = new CognitoUserPool(poolData);
        const currentUser = userPool.getCurrentUser();
        
        if (currentUser) {
          const refreshed = await new Promise((resolve) => {
            currentUser.getSession((err, session) => {
              if (err || !session.isValid()) {
                resolve(false);
                return;
              }
              
              // Update tokens
              const newIdToken = session.getIdToken().getJwtToken();
              const newAuthToken = session.getAccessToken().getJwtToken();
              localStorage.setItem('id_token', newIdToken);
              localStorage.setItem('auth_token', newAuthToken);
              
              // Update request header
              originalRequest.headers.Authorization = `Bearer ${newIdToken}`;
              resolve(true);
            });
          });
          
          if (refreshed) {
            // Retry original request with new token
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
      
      // If refresh fails, redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('id_token');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Adiciona mensagem traduzida ao erro
    if (error.response?.data?.message) {
      error.translatedMessage = translateApiError(error, i18n.t);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Equipment API
export const equipmentAPI = {
  getAll: () => api.get('/equipment'),
  getWithLocation: () => api.get('/equipment/with-location'),
  getById: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id) => api.delete(`/equipment/${id}`),
};

// Barriers API
export const barriersAPI = {
  getAll: () => {
    return api.get('/barriers').then(response => {
      console.log('=== BARRIERS API RESPONSE ===');
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Response data.data:', response.data?.data);
      if (response.data?.data && response.data.data.length > 0) {
        console.log('First barrier sample:', response.data.data[0]);
        console.log('Barrier fields:', Object.keys(response.data.data[0]));
      }
      return response;
    });
  },
  getById: (id) => api.get(`/barriers/${id}`),
  create: (data) => api.post('/barriers', data),
  update: (id, data) => api.put(`/barriers/${id}`, data),
  delete: (id) => api.delete(`/barriers/${id}`),
  getEvents: () => api.get('/barriers/events'),
};

// Suppliers API
export const suppliersAPI = {
  getAll: () => api.get('/suppliers'),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
};

// Devices API
export const devicesAPI = {
  getAll: () => api.get('/devices'),
  getById: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  delete: (id) => api.delete(`/devices/${id}`),
  getStats: () => api.get('/devices/stats'),
};

// Maps API
export const mapsAPI = {
  getAll: () => api.get('/maps'),
  create: (data) => api.post('/maps', data),
  upload: (formData) => api.post('/maps/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/maps/${id}`),
};

// Hospitals API
export const hospitalsAPI = {
  getAll: () => api.get('/hospitals'),
  getById: (id) => api.get(`/hospitals/${id}`),
  create: (data) => api.post('/hospitals', data),
  update: (id, data) => api.put(`/hospitals/${id}`, data),
  delete: (id) => api.delete(`/hospitals/${id}`),
};

// Alarms API
export const alarmsAPI = {
  getAll: () => api.get('/alarms'),
  getStats: () => api.get('/alarms/stats'),
  acknowledge: (id) => api.post(`/alarms/${id}/acknowledge`),
};

// Alerts API
export const alertsAPI = {
  sendAlert: (alertData) => api.post('/notifications/send-alert', alertData),
  getHistory: (params = {}) => api.get('/notifications/history', { params }),
  testEmail: (emailData) => api.post('/notifications/test-email', emailData),
  testSMS: (smsData) => api.post('/notifications/test-sms', smsData),
  getRecentEvents: (params = {}) => api.get('/notifications/history', { params }),
  processPassage: (eventData) => api.post('/events/passage', eventData),
  getStats: (params = {}) => api.get('/notifications/stats', { params })
};

// Analytics API
export const analyticsAPI = {
  getMetrics: (params = {}) => api.get('/analytics/metrics', { params }),
  getComplianceReport: (params = {}) => api.get('/analytics/compliance-report', { params }),
  getExpiredEquipment: (params = {}) => api.get('/analytics/expired-equipment', { params }),
};

// Patients API
export const patientsAPI = {
  getAll: () => api.get('/patients'),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
};

// Staff API
export const staffAPI = {
  getAll: (params = {}) => api.get('/staff', { params }),
  getById: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
};

// Audit Logs API
export const auditAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.action) params.append('action', filters.action);
    if (filters.resource_type) params.append('resource_type', filters.resource_type);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.limit) params.append('limit', filters.limit);
    
    const queryString = params.toString();
    return api.get(`/audit-logs${queryString ? `?${queryString}` : ''}`);
  },

  log: (auditData) => {
    return api.post('/audit-logs', auditData);
  }
};

// Gateway API
export const gatewayAPI = {
  createGateway: (data) => api.post('/iot/gateways', data),
  getGateways: () => api.get('/iot/gateways'),
  deleteGateway: (id) => api.delete(`/iot/gateways/${id}`)
};

export const apiClient = api;
export default api;